import type { Capture, FileEntry, Package } from './types';

/** How one package differs between two snapshots */
export interface PackageChange {
	path: string;
	name: string;
	change: 'added' | 'removed' | 'version' | 'content' | 'same';
	before?: Package;
	after?: Package;
}

/** How one file differs between two archives */
export interface FileChange {
	name: string;
	change: 'added' | 'removed' | 'changed';
	before?: FileEntry;
	after?: FileEntry;
}

/**
 * Two snapshots side by side: which packages were added or removed, which changed version or content
 * (another digest for the same version), which are the same; and, for a package that changed, which files
 * were added, removed or changed, by their digests. Packages are matched by path, as snapshots name them.
 */
export class Comparison {
	static #order = { added: 0, removed: 1, version: 2, content: 3, same: 4 } as const;

	static packages(before: Capture, after: Capture): PackageChange[] {
		const earlier = new Map(before.packages.map(entry => [entry.path, entry]));
		const later = new Map(after.packages.map(entry => [entry.path, entry]));
		const changes: PackageChange[] = [];
		for (const [path, entry] of later) {
			const old = earlier.get(path);
			if (!old) changes.push({ path, name: entry.name, change: 'added', after: entry });
			else if (old.version !== entry.version || old.name !== entry.name) changes.push({ path, name: entry.name, change: 'version', before: old, after: entry });
			else changes.push({ path, name: entry.name, change: old.digest === entry.digest ? 'same' : 'content', before: old, after: entry });
		}
		for (const [path, entry] of earlier) if (!later.has(path)) changes.push({ path, name: entry.name, change: 'removed', before: entry });
		return changes.sort((a, b) => Comparison.#order[a.change] - Comparison.#order[b.change] || a.path.localeCompare(b.path));
	}

	/** Whether two snapshots hold the same packages with the same bytes */
	static same(before: Capture, after: Capture): boolean {
		return Comparison.packages(before, after).every(entry => entry.change === 'same');
	}

	static files(before: FileEntry[], after: FileEntry[]): FileChange[] {
		const only = (entries: FileEntry[]) => new Map(entries.filter(entry => entry.kind === 'file').map(entry => [entry.name, entry]));
		const earlier = only(before);
		const later = only(after);
		const changes: FileChange[] = [];
		for (const [name, entry] of later) {
			const old = earlier.get(name);
			if (!old) changes.push({ name, change: 'added', after: entry });
			else if (old.digest !== entry.digest || old.size !== entry.size) changes.push({ name, change: 'changed', before: old, after: entry });
		}
		for (const [name, entry] of earlier) if (!later.has(name)) changes.push({ name, change: 'removed', before: entry });
		return changes.sort((a, b) => a.name.localeCompare(b.name));
	}
}
